import type { EntryPoint, EntryPointComponent, PreloadFetchPolicy } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { JSResourceForInteraction } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';

import { DEFAULT_CATEGORIES, UPLIFT_DEFAULT_CATEGORIES } from './constants';
import type { BrowseAgentCategory, BrowseAgentExtraProps, BrowseAgentRuntimeProps } from './types';
import { getCategoryById } from './utils';

export type BrowseAgentEntryPointParams = {
	cloudId: string;
	product: string;
	categories?: (
		defaultCategories: readonly BrowseAgentCategory[],
	) => readonly BrowseAgentCategory[];
	initialCategory?: string;
	fetchPolicy?: PreloadFetchPolicy;
};

declare const browseAgentEntryPointType: EntryPoint<
	BrowseAgentEntryPointComponent,
	BrowseAgentEntryPointParams
>;

export type BrowseAgentEntryPoint = typeof browseAgentEntryPointType;

type BrowseAgentEntryPointComponent = EntryPointComponent<
	Record<string, any>,
	Record<string, any>,
	BrowseAgentRuntimeProps,
	BrowseAgentExtraProps
>;

export const browseAgentEntryPoint: BrowseAgentEntryPoint = createEntryPoint({
	root: JSResourceForInteraction<BrowseAgentEntryPointComponent>(() => import(/* webpackChunkName: "@atlaskit-internal_studio-browse-kit_browse-agent" */ "./BrowseAgent"), { moduleId: "d8563351", moduleName: "BrowseAgent", modulePath: "../platform/packages/ai-mate/studio-agent-browse/src/ui/browse-agent/BrowseAgent.tsx", priority: "interaction", moduleId: 'studio-browse-kit_browse-agent',
		}),
	getPreloadProps: ({
		cloudId,
		product,
		categories,
		initialCategory,
		fetchPolicy,
	}: BrowseAgentEntryPointParams) => {
		const defaultCategories = fg('rovo_agents_studio_browse_kit_uplift')
			? UPLIFT_DEFAULT_CATEGORIES
			: DEFAULT_CATEGORIES;
		const resolvedCategories = categories?.(defaultCategories) ?? defaultCategories;
		const category = getCategoryById(resolvedCategories, initialCategory);

		return {
			entryPoints: {
				category: {
					entryPoint: category.entrypoint,
					entryPointParams: {
						categoryId: category.id,
						fetchPolicy,
						cloudId,
						product,
					},
				},
			},
			extraProps: {
				categories: resolvedCategories,
				cloudId,
				initialCategory: category.id,
				product,
			},
		};
	},
});
